import React, { useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';

// Types
import { WorkspaceComment } from 'Shared/types/WorkspaceComment.type';

// Utils
import { getTimeAgoLabel } from 'Shared/utils/DateTime.utils';

// Selectors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';

// Components
import { WpBadge } from 'Wpds/components/badge';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import TextEditor from './TextEditor/TextEditor';
import { WpAvatar, WpAvatarEmpty } from 'Wpds/components/avatar';
import VideoPlayer from 'Shared/components/VideoPlayer/VideoPlayer';
import CommentTextEditor from './CommentTextEditor/CommentTextEditor';

//Styles
import './CommentListItem.scss';

type Props = {
  comment: WorkspaceComment;
  handleDeleteComment?: Function;
  handleUpdateComment?: Function;
  workspaceId: string;
  // Used in meeting mode to trigger the typing participants
  // warning
  setIsEditing?: Function;
};

const CommentListItem = ({ comment, handleDeleteComment, handleUpdateComment, setIsEditing, workspaceId }: Props) => {
  const [isDeleting, toggleIsDeleting] = useToggle(false);
  const [isInputFocused, toggleIsInputFocused] = useToggle(false);
  const [isSavingUpdate, toggleIsSavingUpdate] = useToggle(false);

  const { id: currentUserId } = useCurrentUser();
  const authorId = comment.authorId;
  const slug = comment.uuid;
  const author = useSelector(state => UsersSelectors.getUserByUserId(state, { userId: authorId }))?.toJS();
  const hasAuthor = Boolean(author);

  const authorNameOrEmail = hasAuthor ? author?.fullName || author?.email : '[deactivated user]';
  const messageBody = comment.body;
  const video = comment.video;

  const timeAgoLabel = getTimeAgoLabel(comment.createdAt);

  const showEditIcons = currentUserId === authorId && !isInputFocused && handleDeleteComment && handleUpdateComment;
  const isMeetingNote = comment.meetingModeSessionId;

  const classes = classnames('CommentListItem', isInputFocused ? 'CommentListItem--editing' : false);

  useEffect(() => {
    if (setIsEditing) setIsEditing({ uuid: slug, isEditing: isInputFocused });
  }, [slug, isInputFocused]);

  const handleDeleteClick = useCallback(() => {
    if (!handleDeleteComment) return;
    toggleIsDeleting();

    handleDeleteComment(slug);
  }, [slug]);

  const handleUpdateClick = useCallback(
    ({ body }) => {
      if (!handleUpdateComment) return;
      toggleIsSavingUpdate();
      return handleUpdateComment({ slug, body }).then(() => {
        toggleIsSavingUpdate();
        toggleIsInputFocused();
      });
    },
    [slug]
  );

  return (
    <div className={classes}>
      <div className="CommentListItem__header">
        {hasAuthor ? <WpAvatar avatar={author?.avatar} size="sm" /> : <WpAvatarEmpty size="sm" />}
        <div className="CommentListItem__header__label">
          <span className="CommentListItem__header__label__name">{authorNameOrEmail}</span> - {timeAgoLabel}
        </div>
        {showEditIcons && (
          <div className="CommentListItem__header__actions">
            <WpButtonGroup spacing="sm">
              <WpButton size="sm" variant="tertiary" onClick={toggleIsInputFocused}>
                <WpIcon name="pencil" />
              </WpButton>
              <WpButton size="sm" variant="tertiary" onClick={handleDeleteClick} loading={isDeleting}>
                <WpIcon name="trash" />
              </WpButton>
            </WpButtonGroup>
          </div>
        )}
        {isInputFocused && (
          <WpButton size="sm" variant="tertiary" onClick={toggleIsInputFocused}>
            <WpIcon name="times" />
          </WpButton>
        )}
      </div>

      <div className="CommentListItem__content">
        {isMeetingNote && (
          <div className="CommentListItem__content__meeting-note-label">
            <WpBadge color="blue" size="sm">
              Meeting Notes
            </WpBadge>
          </div>
        )}
        {video ? (
          <div className="comment-item-video">
            <VideoPlayer
              videoID={video.id}
              sources={[video.mp4Url, video.url]}
              previewImageURL={video.previewImageUrl}
              transcription={video.transcription}
              audioOnly={video.audioOnly}
            />
          </div>
        ) : (
          <>
            {isInputFocused ? (
              <CommentTextEditor
                handleCreateComment={handleUpdateClick}
                toolbarPlacement="bottom"
                hideVideo={true}
                showHint={false}
                showSaveButton
                workspaceId={workspaceId}
                outerEditorState={messageBody}
                isSaving={isSavingUpdate}
                enterToSend={false}
                // autoFocus
              />
            ) : (
              <TextEditor
                focused={false}
                setFocused={toggleIsInputFocused}
                body={messageBody}
                workspaceId={workspaceId}
                onSave={handleUpdateClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CommentListItem;
